import dayjs from 'dayjs';
import { AVAILABILITY_LABELS, ENGINE_TYPE_LABELS, SPECIFICATION_LABELS, STATUS_LABELS } from '../../utils/labels';
import { appNow, appNowTimestamp, toWallClockTimestamp } from '../../utils/timezone';
import type {
  AvailabilityKey,
  Booking,
  BookingPickupState,
  BookingResizeEdge,
  EngineTypeKey,
  Machine,
  SpecificationKey,
  StatusKey,
} from './types';

// Width in px of one day-column in the planning grid. Shared by PlanningGrid.tsx (rendering
// booking bars/day headers) and useBookingResize.ts (pixel→day snapping while dragging).
export const DAY_WIDTH = 36;

export const STATUSES = {
  to_order: {
    label: STATUS_LABELS.to_order,
    color: 'bg-stone-100 text-stone-600 dark:bg-stone-800 dark:text-stone-300',
    dot: 'bg-stone-400 dark:bg-stone-500',
  },
  ordered: {
    label: STATUS_LABELS.ordered,
    color: 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300',
    dot: 'bg-blue-500 dark:bg-blue-400',
  },
  collected: {
    label: STATUS_LABELS.collected,
    color: 'bg-emerald-100 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-300',
    dot: 'bg-emerald-500 dark:bg-emerald-400',
  },
} as const;

export const AVAILABILITY = {
  available: {
    label: AVAILABILITY_LABELS.available,
    color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300',
    dot: 'bg-emerald-500 dark:bg-emerald-400',
  },
  in_use: {
    label: AVAILABILITY_LABELS.in_use,
    color: 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300',
    dot: 'bg-amber-500 dark:bg-amber-400',
  },
  reserved: {
    label: AVAILABILITY_LABELS.reserved,
    color: 'bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-300',
    dot: 'bg-sky-500 dark:bg-sky-400',
  },
  inactive: {
    label: AVAILABILITY_LABELS.inactive,
    color: 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300',
    dot: 'bg-red-500 dark:bg-red-400',
  },
} as const;

export const ENGINE_TYPES = {
  none: {
    label: ENGINE_TYPE_LABELS.none,
  },
  electric: {
    label: ENGINE_TYPE_LABELS.electric,
  },
  diesel: {
    label: ENGINE_TYPE_LABELS.diesel,
  },
  hybrid: {
    label: ENGINE_TYPE_LABELS.hybrid,
  },
} as const;

// Fixed catalog of measurable machine specifications an org can enable per category (see
// CategorySpecification/MachineSpecificationValue in types.ts) — hardcoded here, not
// DB-driven, the same way STATUSES/ENGINE_TYPES are. `unit` is null for count-only specs
// (person_count); `decimals` governs both input step and export/import rounding.
export const SPECIFICATION_DEFINITIONS: Record<SpecificationKey, { unit: 'kg' | 'm' | null; decimals: 0 | 2 }> = {
  max_lift_capacity: { unit: 'kg', decimals: 0 },
  drive_through_height: { unit: 'm', decimals: 2 },
  drive_through_width: { unit: 'm', decimals: 2 },
  max_working_reach: { unit: 'm', decimals: 2 },
  weight: { unit: 'kg', decimals: 0 },
  length: { unit: 'm', decimals: 2 },
  width: { unit: 'm', decimals: 2 },
  max_length_per_stamp: { unit: 'm', decimals: 2 },
  lift_capacity: { unit: 'kg', decimals: 0 },
  person_count: { unit: null, decimals: 0 },
  loading_bed_capacity: { unit: 'kg', decimals: 0 },
  mast_drive_through_height: { unit: 'm', decimals: 2 },
};

// Sorted A-Z by Dutch label (not catalog/insertion order) — drives both the order options are
// offered in category-specifications pickers and which key "add specification" defaults to.
export const SPECIFICATION_KEYS = (Object.keys(SPECIFICATION_DEFINITIONS) as SpecificationKey[]).sort(
  (a, b) => SPECIFICATION_LABELS[a].localeCompare(SPECIFICATION_LABELS[b], 'nl')
);

// e.g. "Doorrijhoogte (m)" / "Aantal personen" — reused by the machine form's field labels,
// the category-specifications config UI, and the import/export column headers so unit
// formatting stays in one place.
export function formatSpecificationLabel(key: SpecificationKey): string {
  const { unit } = SPECIFICATION_DEFINITIONS[key];
  return unit ? `${SPECIFICATION_LABELS[key]} (${unit})` : SPECIFICATION_LABELS[key];
}

// e.g. "12,50 m" / "3" (person_count has no unit) — reused by the detail panel's required-spec
// chips and optional-spec rows, formatted to the key's own decimal precision, not the raw
// stored float, so e.g. weight (0 decimals) never shows a stray ".0".
export function formatSpecificationValue(key: SpecificationKey, value: number): string {
  const { unit, decimals } = SPECIFICATION_DEFINITIONS[key];
  const formatted = value.toLocaleString('nl-BE', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  return unit ? `${formatted} ${unit}` : formatted;
}

export type { AvailabilityKey, EngineTypeKey, StatusKey };

// Per-booking pickup/hand-in state, only meaningful when the org's pickup/hand-in check is
// enabled. A booking is "reserved" (pickup-confirmable) when EITHER:
//   1. its start_date is no more than one calendar day in the past — an early pickup ahead of
//      the planned start is always fine (no upper bound on the future), but a booking whose
//      start slipped by more than a day without ever being picked up is treated as needing to
//      be rescheduled rather than still confirmable as-is; OR
//   2. it's still currently running — started more than a day ago, but its own planned end
//      hasn't passed yet (e.g. a 15-day booking that started 10 days ago and doesn't end for
//      another 5) — pickup should never be blocked just because it's a long booking that
//      started a while back.
// See CLAUDE.md's "Pickup/Hand-in Confirmation (Planning)" section for the full rule set —
// keep that doc in sync with any change here.
// Once pickup is confirmed the booking stays "in_use" indefinitely, with no date restriction
// at all — it can only leave that state via an explicit hand-in, which is always confirmable.
export function getBookingPickupState(
  bk: Booking,
  pickupHandinEnabled: boolean,
  today: string = appNow().format('YYYY-MM-DD'),
  nowMs: number = appNowTimestamp()
): BookingPickupState | null {
  if (!pickupHandinEnabled) return null;
  if (bk.handin_confirmed_at) return 'returned';
  if (bk.pickup_confirmed_at) return 'in_use';
  const yesterday = dayjs(today).subtract(1, 'day').format('YYYY-MM-DD');
  if (bk.start_date >= yesterday) return 'reserved';
  const end = toWallClockTimestamp(bk.end_date, bk.end_time);
  if (end !== undefined && end >= nowMs) return 'reserved';
  return null;
}

// True once a booking that was picked up should already have been handed in
// (its window has elapsed) but hasn't been — the "needs action" case surfaced
// on the dashboard. Gated on pickupHandinEnabled like getBookingPickupState: if an org
// turns the check back off, any pickup_confirmed_at/handin_confirmed_at left over from
// when it was on must stop driving warnings/visuals — this must never fire on its own.
export function isBookingOverdueReturn(
  bk: Booking,
  pickupHandinEnabled: boolean,
  nowMs: number = appNowTimestamp()
): boolean {
  if (!pickupHandinEnabled) return false;
  if (!bk.pickup_confirmed_at || bk.handin_confirmed_at) return false;
  const end = toWallClockTimestamp(bk.end_date, bk.end_time);
  return end !== undefined && nowMs > end;
}

export function getMachineAvailability(machine: Machine, pickupHandinEnabled: boolean): AvailabilityKey {
  if (machine.inactive) return 'inactive';
  const today = appNow().format('YYYY-MM-DD');
  if (machine.start_date && machine.start_date > today) return 'inactive';
  if (machine.end_date && machine.end_date < today) return 'inactive';
  if (machine.status === 'to_order') return 'inactive';

  const nowMs = appNowTimestamp();
  const bookings = machine.bookings ?? [];

  if (!pickupHandinEnabled) {
    // "in_use" reflects the exact moment, not just the calendar day: a booking that already
    // ended earlier today (or hasn't started yet today) must not keep blocking the machine —
    // otherwise it looks taken to whoever asks next, even though it's free right now.
    const active = bookings.some((bk) => {
      const start = toWallClockTimestamp(bk.start_date, bk.start_time);
      const end = toWallClockTimestamp(bk.end_date, bk.end_time);
      return start !== undefined && end !== undefined && start <= nowMs && nowMs <= end;
    });
    return active ? 'in_use' : 'available';
  }

  const states = bookings.map((bk) => getBookingPickupState(bk, true, today, nowMs));
  if (states.includes('in_use')) return 'in_use';
  if (states.includes('reserved')) return 'reserved';
  return 'available';
}

// A booking can't actually be picked up while another booking on the same machine is still
// physically out (picked up, not yet handed in) — regardless of whether their planned dates
// overlap, since an overrunning booking blocks the machine for real. Returns the blocking
// booking (for the warning message) or null when pickup is safe to confirm.
export function getBlockingInUseBooking(booking: Booking, machineBookings: Booking[]): Booking | null {
  return (
    machineBookings.find((other) => other.id !== booking.id && getBookingPickupState(other, true) === 'in_use') ??
    null
  );
}

const toDateTimeKey = (date: string, time: string) => `${date}T${time.slice(0, 5)}`;

// Whether dragging one edge of a booking to newDate produces a valid result: not inverted,
// within the machine's own availability window, and not overlapping another booking on the
// same machine. Mirrors BookingModal.tsx's overlapsWith/toDateTimeKey comparison idiom.
export function isValidBookingResize(
  booking: Booking,
  edge: BookingResizeEdge,
  newDate: string,
  machine: Machine
): boolean {
  const newStartDate = edge === 'start' ? newDate : booking.start_date;
  const newEndDate = edge === 'end' ? newDate : booking.end_date;
  const startKey = toDateTimeKey(newStartDate, booking.start_time);
  const endKey = toDateTimeKey(newEndDate, booking.end_time);
  if (startKey >= endKey) return false;
  if (edge === 'start' && machine.start_date && newStartDate < machine.start_date) return false;
  if (edge === 'end' && machine.end_date && newEndDate > machine.end_date) return false;

  const overlaps = (machine.bookings ?? []).some((other) => {
    if (other.id === booking.id) return false;
    const otherStart = toDateTimeKey(other.start_date, other.start_time);
    const otherEnd = toDateTimeKey(other.end_date, other.end_time);
    return startKey < otherEnd && otherStart < endKey;
  });
  return !overlaps;
}

export type BookingPeriod = 'past' | 'active' | 'future';

// Groups a booking into active/gepland/afgelopen for the machine panel's booking lists.
// A booking that's picked up but not yet handed in stays "active" indefinitely — even once
// its calendar end_date has passed — so it doesn't silently fall into "Afgelopen" while
// another booking's calendar window makes it "active" too.
export function getBookingPeriod(
  bk: Booking,
  pickupHandinEnabled: boolean,
  today: string = appNow().format('YYYY-MM-DD')
): BookingPeriod {
  if (getBookingPickupState(bk, pickupHandinEnabled, today) === 'in_use') return 'active';
  if (bk.end_date < today) return 'past';
  if (bk.start_date > today) return 'future';
  return 'active';
}
